<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">Produkty</span>
        <h3 class="page-title">Lista produktów</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="warning"
          @click.prevent="exportPrices()"
          :disabled="exportInProgress">
          <div
            v-if="exportInProgress"
            class="spinner-border spinner-border-sm"
            role="status"></div>
          <template v-else>
            <i class="material-icons">download</i>
            Eksportuj cenniki
          </template>
        </d-button>

        <d-button
          theme="primary"
          @click.prevent="openImportPopup()"
          class="ml-2">
          <i class="material-icons">upload</i>
          Importuj cenniki
        </d-button>

        <d-button
          theme="success"
          @click.prevent="$router.push('/produkty/nowy')"
          class="ml-2">
          <i class="material-icons">add</i>
          Dodaj produkt
        </d-button>
      </d-col>
    </d-row>

    <d-row
      v-if="loaded"
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <validation-observer
        ref="products-filters-form"
        tag="form"
        style="width: 100%;"
        class="d-flex">
        <d-input
          placeholder="Podaj nazwę produktu"
          v-model="filters.search" />

        <d-button
          theme="info"
          outline
          class="text-nowrap ml-2"
          @click.prevent="validateFiltersForm">Szukaj</d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap ml-2"
          @click.prevent="clearFilters">Resetuj filtry</d-button>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0">Nazwa produktu</th>
            <th scope="col" class="border-0">Kod produktu</th>
            <th scope="col" class="border-0">Kod ceny</th>
            <th scope="col" class="border-0">Akcje</th>
          </tr>
        </thead>
        <tbody v-if="loaded">
          <tr
            v-for="(product, index) of products"
            :key="'product-' + index">
            <td class="align-middle">
              {{ product.name }}
              <template v-if="product.intro">
                <br>
                <small>{{ product.intro }}</small>
              </template>
            </td>
            <td class="align-middle">
              {{ product.code }}
            </td>
            <td class="align-middle">
              {{ product.price_code }}
            </td>
            <td
              class="align-middle"
              style="width: 200px;">
              <d-button
                theme="primary"
                @click.native="$router.push('/produkty/edycja/' + product.id)"
                class="mr-2"
                title="Edytuj">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                theme="info"
                :disabled="!!copyInProgress"
                @click.native="copyProduct(product.id)"
                class="mr-2"
                title="Kopiuj produkt">
                <div
                  v-if="copyInProgress === product.id"
                  class="spinner-border spinner-border-sm"
                  role="status"></div>
                <i
                  v-else
                  class="material-icons">
                  content_copy
                </i>
              </d-button>
              <d-button
                theme="danger"
                :disabled="!!removeInProgress"
                @click.native="removeProduct(product.id)"
                title="Usuń">
                <div
                  v-if="removeInProgress === product.id"
                  class="spinner-border spinner-border-sm"
                  role="status"></div>
                <i
                  v-else
                  class="material-icons">
                  delete
                </i>
              </d-button>
            </td>
          </tr>
          <tr v-if="products.length === 0">
            <td
              colspan="5"
              class="text-center">
              Brak produktów do wyświetlenia&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!loaded" class="pb-3 pt-3 text-center">
        Trwa ładowanie danych&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        Wczytywanie danych nie powiodło się. <a href="javascript:window.location.reload();" class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />

    <d-modal
      v-if="showImportPopup"
      @close="closeImportPopup">
      <d-modal-header>
        <d-modal-title>
          Importuj plik z cennikami produktów
        </d-modal-title>
      </d-modal-header>
      <d-modal-body>
        <d-form>
          <validation-observer ref="import-form" tag="form">
            <div class="form-group">
              <label for="name">
                Plik cennika:
              </label>

              <validation-provider
                rules="required"
                name="file"
                v-slot="{ errors }">
                <file-input
                  :onChange="setNewFile"
                  id="inputFileImport" />
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>
          </validation-observer>
        </d-form>
      </d-modal-body>
      <d-modal-footer>
        <d-button
          class="ml-0"
          theme="danger"
          :disabled="importInProgress"
          @click.prevent="closeImportPopup">
          Anuluj
        </d-button>
        <d-button
          class="ml-auto"
          theme="success"
          :disabled="importInProgress"
          @click.prevent="validateImportForm">
          <div
            v-if="importInProgress"
            class="spinner-border spinner-border-sm"
            role="status"></div>
          Importuj
        </d-button>
      </d-modal-footer>
    </d-modal>
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import jsonToFormData from 'json-form-data';
import FormUtils from './../utils/FormUtils.js';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';
import Vue from 'vue';

export default {
  name: 'products',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  data() {
    return {
      products: [],
      loaded: false,
      loadError: false,
      copyInProgress: false,
      removeInProgress: false,
      filters: {
        search: ''
      },
      exportInProgress: false,
      importFile: null,
      importInProgress: false,
      showImportPopup: false
    };
  },
  mounted () {
    this.loadFilteredData();
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = {
        search: this.filters.search
      };

      ListUtils.loadItemsData(this, {
        endpoint: '/products/items?page=' + this.currentPage,
        listField: 'products',
        pagination: true,
        params: {
          where
        }
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        search: ''
      };

      this.loadFilteredData();
      this.$bus.$emit('view-filters-reset');
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['products-filters-form'], this.debouncedLoadFilteredData);
    },
    copyProduct (productID) {
      this.copyInProgress = productID;

      FormUtils.submitInBackground(this, {
        endpoint: '/products/copy/' + productID,
        errorTitle: 'Wystąpił błąd',
        errorText: 'Kopiowanie produktu nie powiodło się. Spróbuj ponownie',
        successAction: (data) => {
          if (data.success) {
            this.copyInProgress = false;
            this.loadFilteredData();
          } else {
            Vue.swal({
              title: 'Wystąpił błąd',
              text: 'Kopiowanie produktu nie powiodło się. Spróbuj ponownie',
              icon: 'warning',
              confirmButtonText: 'OK'
            });

            this.copyInProgress = false;
            this.loadFilteredData();
          }
        },
        failAction: () => {
          this.copyInProgress = false;
          this.loadFilteredData();
        }
      });
    },
    removeProduct (productID) {
      this.removeInProgress = productID;

      ListUtils.removeItem(this, {
        endpointBase: '/products/delete/',
        id: productID,
        successTitle: 'Usunięto produkt',
        successText: 'Wybrany produkt został usunięty',
        confirmTitle: 'Czy na pewno chcesz usunąć ten produkt?',
        confirmText: 'Tej operacji nie można cofnąć',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Usuwanie produktu nie powiodło się. Spróbuj ponownie.',
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    openImportPopup () {
      this.showImportPopup = true;
    },
    closeImportPopup () {
      this.importFile = null;
      this.importSize = null;
      this.showImportPopup = false;
    },
    validateImportForm () {
      FormUtils.validate(this.$refs['import-form'], this.importPrices);
    },
    setNewFile (event) {
      this.importFile = event.target.files[0];
    },
    importPrices () {
      this.importInProgress = true;

      let importData = {
        file: this.importFile
      };

      this.$http.request({
        method: 'POST',
        url: '/products/import-prices',
        headers: { 'Content-Type': undefined },
        transformRequest (data) {
          let dataToSend = jsonToFormData(data);
          return dataToSend;
        },
        data: importData
      }).then((response) => {
        if (response.data.success) {
          Vue.swal({
            title: 'Zaimportowano cenniki produktów',
            html: 'Udało się pomyślnie zaimportować cenniki produktów',
            icon: 'success',
            confirmButtonText: 'OK',
            buttonsStyling: true,
            timer: 2000
          });
        }

        this.closeImportPopup();
        this.clearFilters();
        this.importInProgress = false;
      }).catch(error => {
        console.log(error);
        this.closeImportPopup();

        Vue.swal({
          title: 'Import nie powiódł się',
          html: 'Operacja importu cenników produktów nie powiodła się. Spróbuj ponownie...',
          icon: 'error',
          confirmButtonText: 'OK',
          buttonsStyling: true
        });
        this.importInProgress = false;
      });
    },
    exportPrices () {
      this.exportInProgress = true;

      this.$http.post('/products/export-prices', {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'cenniki-produktow.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.exportInProgress = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: 'Eksport nie powiódł się',
          html: 'Wyeksportowanie cenników produktów nie powiodło się. Spróbuj ponownie...',
          type: 'warning',
          confirmButtonText: 'OK',
          buttonsStyling: true
        });
        this.exportInProgress = false;
      });
    }
  }
};
</script>

